import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import logo1 from "../../../../assets/Image (3).jpg";
import {
  Send,
  User,
  Download,
  CheckSquare,
  Square,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
const BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL
const API_CODE = process.env.REACT_APP_API_CODE;


const FalconAIChatbot = ({ fileId, onRefreshTable }) => {
  // --- Core state (single in-memory chat session) ---
  const [userMappingFileId, setUserMappingFileId] = useState(null);
  const [messages, setMessages] = useState([]); // [{ id, text, sender, timestamp, hasDownload, originalQuery, data }]
  const [message, setMessage] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [approvingMessageId, setApprovingMessageId] = useState(null);
  const [approvalInProgress, setApprovalInProgress] = useState(false);
  const [organizationConfigId, setOrganizationConfigId] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
   const [visibleRows, setVisibleRows] = useState({});
  const INITIAL_ROW_LIMIT = 10;
  const [currentFileName, setCurrentFileName] = useState(() => {
    return sessionStorage.getItem("selectedFileName") || "Mapping Assistant";
  });

  // --- refs & helpers ---
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const parseMarkdown = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const content = part.slice(2, -2);
        return <strong key={`strong-${content}`}>{content}</strong>;
      }
      return <span key={`span-${part}`}>{part}</span>;
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Get current user email
  const getCurrentUserEmail = () => {
    try {
      const accountKey = JSON.parse(sessionStorage.getItem("msal.account.keys"))?.[0];
      if (accountKey) {
        const accountData = JSON.parse(sessionStorage.getItem(accountKey));
        return accountData?.username || null;
      }
      return null;
    } catch (error) {
      console.error("Error getting current user email:", error);
      return null;
    }
  };

  // Fetch and match user ID
  const fetchCurrentUserId = async () => {
    try {
      const currentUserEmail = getCurrentUserEmail();

      if (!currentUserEmail) {
        return null;
      }
      const response = await axiosClient.get("/api/User/all");

      if (!response?.data) {
        return null;
      }

      const allUsers = Array.isArray(response.data) ? response.data : [];
      const normalizedCurrentEmail = currentUserEmail.toLowerCase().trim();

      let matchedUser = allUsers.find(
        (user) => user.email && user.email.toLowerCase().trim() === normalizedCurrentEmail
      );

      if (!matchedUser) {
        matchedUser = allUsers.find((user) => {
          if (!user.email) return false;
          const normalizedUserEmail = user.email.toLowerCase().trim();
          return (
            normalizedUserEmail.includes(normalizedCurrentEmail) ||
            normalizedCurrentEmail.includes(normalizedUserEmail)
          );
        });
      }

      if (matchedUser) {
        const userId = matchedUser.userId || matchedUser.id || null;
        return userId;
      }

      return null;
    } catch (error) {
      console.error("Error fetching user ID:", error);
      return null;
    }
  };



  // Fetch user ID on component mount
  useEffect(() => {
    const loadUserId = async () => {
      const userId = await fetchCurrentUserId();
      setCurrentUserId(userId);
    };
    loadUserId();
  }, []);

  useEffect(() => {
    const storedOrgConfigId = localStorage.getItem("organization_config_id");
    if (storedOrgConfigId) {
      setOrganizationConfigId(storedOrgConfigId);
    }
  }, []);


  //  FIX: Update file ID and reset chat when fileId prop changes
  useEffect(() => {
    if (fileId) {
      setUserMappingFileId(fileId);

      // Get the current file name from session storage
      const storedFileName = sessionStorage.getItem("selectedFileName");
      const fileName = storedFileName || "Mapping Assistant";
      setCurrentFileName(fileName);

      // Reset messages with new welcome message
      const template = localStorage.getItem("template");
      const mappingText = template || "SAP S/4HANA → AX mapping queries";
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your Mapping assistant for **${fileName}**. I can help you with:  
- ${mappingText} mapping queries`,
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages([welcome]);

      // Clear any selected rows
      setSelectedRows({});

      // Clear message input
      setMessage("");
    }
  }, [fileId]);

  //  FIX: Listen for file changes in session storage (cross-component sync)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedFileId = sessionStorage.getItem("selectedFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");

      if (storedFileId && Number.parseInt(storedFileId, 10) !== userMappingFileId) {
        setUserMappingFileId(Number.parseInt(storedFileId, 10));
        setCurrentFileName(storedFileName || "Mapping Assistant");



        // Reset chat with new welcome message
        const template = localStorage.getItem("template");
        const mappingText = template || "SAP S/4HANA → AX mapping queries";
        const welcome = {
          id: Date.now(),
          sender: "ai",
          text: `👋 Hello! I am your Mapping assistant for **${storedFileName}**. I can help you with:  
- ${mappingText} mapping queries`,
          timestamp: new Date(),
          hasDownload: false,
        };
        setMessages([welcome]);
        setSelectedRows({});
        setMessage("");
      }
    };

    // Listen for custom storage events
    // eslint-disable-next-line no-undef
    globalThis.addEventListener("storage", handleStorageChange);
    // eslint-disable-next-line no-undef
    globalThis.addEventListener("customStorageChange", handleStorageChange);

    return () => {
      // eslint-disable-next-line no-undef
      globalThis.removeEventListener("storage", handleStorageChange);
      // eslint-disable-next-line no-undef
      globalThis.removeEventListener("customStorageChange", handleStorageChange);
    };
  }, [userMappingFileId]);

  // Initial load - only if no fileId prop provided
  useEffect(() => {
    if (!fileId) {
      const storedFileId = sessionStorage.getItem("selectedFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");

      if (storedFileId) {
        setUserMappingFileId(Number.parseInt(storedFileId, 10));
        setCurrentFileName(storedFileName || "Mapping Assistant");
      }
    }
  }, [fileId]);

  // Initial welcome message - only on first mount if no messages
  useEffect(() => {
    if (messages.length === 0) {
      const template = localStorage.getItem("template");
      const mappingText = template || "SAP S/4HANA → AX mapping queries";
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your Mapping assistant for **${currentFileName}**. I can help you with:  
- ${mappingText} mapping queries`,
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages([welcome]);
    }
  }, []);

  const handleLoadMore = (messageId) => {
    setVisibleRows(prev => ({
      ...prev,
      [messageId]: true
    }));
  };

  // ------------- Selection helpers (checkboxes for rows) -------------
  const handleRowSelect = (messageId, rowIndex) => {
    const key = `${messageId}-${rowIndex}`;
    setSelectedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = (messageId, totalRows) => {
    const allSelected = Array.from({ length: totalRows }).every(
      (_, i) => selectedRows[`${messageId}-${i}`]
    );

    const newSelection = { ...selectedRows };
    Array.from({ length: totalRows }).forEach((_, i) => {
      newSelection[`${messageId}-${i}`] = !allSelected;
    });
    setSelectedRows(newSelection);
  };




  const handleApproveSelected = async (messageId, selectedData) => {

    if (approvalInProgress) {
      return;
    }

    try {
      setApprovingMessageId(messageId);
      setApprovalInProgress(true);

      console.log("Approve clicked for message:", messageId);
      console.log("Selected rows data:", selectedData);

      const updateBulkMapperDetailsRequests = selectedData.map((row) => ({
        targetTableName: row.sap_table_name || row.SAPTableName || row["SAP Table Name"] || "",
        targetFieldName: row.sap_column_name || row.SAPColumnName || row["SAP Column Name"] || "",
        sourceTableName: row.ax_table_name || row.AXTableName || row["AX Table Name"] || "",
        sourceFieldName: row.ax_field_name || row.AXColumnName || row["AX Column Name"] || "",
      }));

      const payload = {
        updateBulkMapperDetailsRequests,
        userMappingFileId: userMappingFileId,
      };

      console.log("Sending payload to API:", payload);

      const response = await axiosClient.post(
        "/api/Mapper/UpdateBulkMapperFile",
        payload
      );

      console.log("Update response status:", response.status);



      toast.success("✅ Selected mappings approved and saved successfully!");
      if (typeof onRefreshTable === "function") {
  onRefreshTable();
}


      setSelectedRows((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (key.startsWith(`${messageId}-`)) {
            delete updated[key];
          }
        });
        return updated;
      });
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error(`❌ Failed to approve mappings: ${error.message}`);
      setApprovingMessageId(null);
    } finally {
      setApprovingMessageId(null);

      setApprovalInProgress(false);
    }
  };

  // ------------- Agent API integration -------------
  const callAgentAPI = async (query) => {
    try {
      // Retrieve mapping file ID from session storage
      const mappingFileId = sessionStorage.getItem("selectedFileId");

      if (!mappingFileId) {
        return {
          success: false,
          error:
            "Mapping file ID not found. Please select a mapping file first.",
          hasDownloadableData: false,
        };
      }


      const response = await fetch(`${BASE_URL}/api/v1/map/object-driven?code=${API_CODE}`,
        {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query,
          user_id: currentUserId,
          thread_id: 0,
          mapping_file_id: mappingFileId,
          min_confidence: 0,
          organization_config_id: organizationConfigId,
          // download_format: "",
          // filename: "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (errorData) {
          // Extract the error message from the response
          const errorMessage = errorData.message || errorData.detail || `HTTP error! status: ${response.status}`;
          return {
            success: false,
            error: errorMessage,
            errorDetails: errorData,
            hasDownloadableData: false,
          };
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data,
        hasDownloadableData: true,
      };
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        error: error.message,
        hasDownloadableData: false,
      };
    }
  };

  // ------------- File generation & download fallback -------------
  const findTableDataInMessage = (messageData) => {
    if (Array.isArray(messageData)) {
      return messageData;
    }
    if (messageData && typeof messageData === "object") {
      const commonArrayKeys = ["results", "sap_columns", "sap_tables", "data"];
      for (const key of commonArrayKeys) {
        if (Array.isArray(messageData[key]) && messageData[key].length > 0) {
          return messageData[key];
        }
      }
      const dataKeys = Object.keys(messageData);
      for (const key of dataKeys) {
        if (Array.isArray(messageData[key]) && messageData[key].length > 0) {
          return messageData[key];
        }
      }
    }
    return null;
  };

  const generateCSVContent = (tableData) => {
    if (typeof tableData[0] === "object" && tableData[0] !== null) {
      const allKeys = [
        ...new Set(tableData.flatMap((item) => Object.keys(item))),
      ];
      return [
        allKeys.join(","),
        ...tableData.map((item) =>
          allKeys
            .map((header) => {
              const value = item[header] || "";
              return `"${String(value).replaceAll('"', '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");
    }
    return [
      "Index,Value",
      ...tableData.map(
        (item, index) =>
          `${index + 1},"${String(item).replaceAll('"', '""')}"`
      ),
    ].join("\n");
  };

  const downloadFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    // eslint-disable-next-line no-undef
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // eslint-disable-next-line no-undef
    globalThis.URL.revokeObjectURL(url);
  };

  const generateFileFromDisplayedData = (messageData, format, filename) => {
    try {
      const tableData = findTableDataInMessage(messageData);

      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        throw new Error(
          "No valid data to export - no arrays found in the data"
        );
      }

      if (format === "csv") {
        const csvContent = generateCSVContent(tableData);
        downloadFile(csvContent, filename);
        return { success: true };
      }
      throw new Error("Only CSV format is supported in fallback mode");
    } catch (error) {
      console.error("generateFileFromDisplayedData error:", error);
      return { success: false, error: error.message };
    }
  };

  const extractTableDataFromJSON = (jsonData) => {
    if (Array.isArray(jsonData)) {
      return jsonData;
    }
    if (Array.isArray(jsonData?.results)) {
      return jsonData.results;
    }
    if (Array.isArray(jsonData?.sap_columns)) {
      return jsonData.sap_columns;
    }
    const arrayKeys = Object.keys(jsonData).filter(
      (key) => Array.isArray(jsonData[key]) && jsonData[key].length > 0
    );
    return arrayKeys.length > 0 ? jsonData[arrayKeys[0]] : null;
  };

  const createCSVFromTableData = (tableData) => {
    const allKeys = [
      ...new Set(tableData.flatMap((item) => Object.keys(item))),
    ];
    return [
      allKeys.join(","),
      ...tableData.map((item) =>
        allKeys
          .map((header) => {
            const value = item[header] || "";
            return `"${String(value).replaceAll('"', '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");
  };

  const downloadCSVFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    // eslint-disable-next-line no-undef
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      // eslint-disable-next-line no-undef
      globalThis.URL.revokeObjectURL(url);
      a.remove();
    }, 100);
  };

  const downloadResponse = async (query, format = "csv") => {
    try {
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replaceAll(":", "-");
      const filename = `mapping_result_${timestamp}`;

      const mappingFileId = sessionStorage.getItem("selectedFileId");
      if (!mappingFileId) {
        throw new Error("Mapping file ID not found in session storage");
      }

      const response = await fetch(`${BASE_URL}/api/v1/map/object-driven?code=${API_CODE}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query,
          mapping_file_id: mappingFileId,
          // download_format: format,
          // filename: filename,
          user_id: currentUserId,
          thread_id: 0,
          min_confidence: 0,
          organization_config_id: organizationConfigId,

        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Download Error Response:", errorText);
        throw new Error(`Download failed! status: ${response.status}`);
      }

      if (format === "csv") {
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          const jsonData = await response.json();
          const tableData = extractTableDataFromJSON(jsonData);
          if (!tableData || tableData.length === 0) {
            throw new Error(
              "No valid data found in API response for CSV conversion"
            );
          }
          const csvContent = createCSVFromTableData(tableData);
          downloadCSVFile(csvContent, filename);
        } else {
          const csvText = await response.text();
          downloadCSVFile(csvText, filename);
        }
        return { success: true };
      }

      return { success: false, error: "Unsupported format" };
    } catch (error) {
      console.error("Download Error Details:", error);
      return {
        success: false,
        error: error.message || "Download failed due to network error",
      };
    }
  };

  // ------------- Table renderer (unchanged logic, but using messages) -------------
  const formatTableName = (key) => {
    return key.replaceAll("_", " ").replaceAll(/\b\w/g, (l) => l.toUpperCase());
  };

  const processArrayData = (data) => {
    if (data.length > 0 && typeof data[0] === "object" && data[0] !== null) {
      return { tableData: data, tableName: "Results" };
    }
    return {
      tableData: data.map((item, index) => ({
        Index: index + 1,
        Value: item,
      })),
      tableName: "Results",
    };
  };

  const processObjectData = (data) => {
    if (
      (data.message && typeof data.message === "string" && Object.keys(data).length === 1) ||
      (data.detail && typeof data.detail === "string" && Object.keys(data).length === 1)
    ) {
      return null;
    }

    const arrayKeys = Object.keys(data).filter(
      (key) => Array.isArray(data[key]) && data[key].length > 0
    );

    if (arrayKeys.length > 0) {
      const firstArrayKey = arrayKeys[0];
      const firstArray = data[firstArrayKey];
      const formattedName = formatTableName(firstArrayKey);

      if (firstArray[0] && typeof firstArray[0] === "object" && firstArray[0] !== null) {
        return { tableData: firstArray, tableName: formattedName };
      }
      return {
        tableData: firstArray.map((item, index) => ({
          Index: index + 1,
          [formattedName]: item,
        })),
        tableName: formattedName,
      };
    }

    const objectKeys = Object.keys(data);
    if (objectKeys.length > 0) {
      return {
        tableData: objectKeys.map((key) => ({
          Property: formatTableName(key),
          Value:
            typeof data[key] === "object" && data[key] !== null
              ? JSON.stringify(data[key])
              : String(data[key] || ""),
        })),
        tableName: "Details",
      };
    }

    return null;
  };

  const extractTableData = (data) => {
    if (Array.isArray(data)) {
      return processArrayData(data);
    }
    if (typeof data === "object" && data !== null) {
      return processObjectData(data);
    }
    return null;
  };

  const getStatusBadgeClass = (displayValue) => {
    if (displayValue.includes("✅") || displayValue.toLowerCase().includes("mapped")) {
      return "bg-green-100 text-green-800";
    }
    if (displayValue.includes("❌") || displayValue.toLowerCase().includes("not mapped")) {
      return "bg-red-100 text-red-800";
    }
    if (displayValue.includes("⚠️") || displayValue.toLowerCase().includes("partial")) {
      return "bg-yellow-100 text-yellow-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const getMandatoryBadgeClass = (displayValue) => {
    const lowerValue = displayValue.toLowerCase();
    return lowerValue === "yes" || lowerValue === "true"
      ? "bg-red-100 text-red-800"
      : "bg-gray-100 text-gray-800";
  };

  const renderTableFromJSON = (data, messageId) => {
    try {
      if (!data) return null;

      const extracted = extractTableData(data);
      if (!extracted) return null;

      const { tableData, tableName } = extracted;

      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        return null;
      }

      const validTableData = tableData.filter(
        (item) => item && typeof item === "object" && item !== null
      );
      if (validTableData.length === 0) return null;

      const getConfidenceBadgeClass = (confidenceValue) => {
        const numValue = Number.parseFloat(confidenceValue);
        if (Number.isFinite(numValue)) {
          const percentage = numValue * 100;
          return percentage >= 80
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800";
        }
        return "bg-gray-100 text-gray-800";
      };

      // Define columns to hide
      const columnsToHide = new Set(['id', 'mapping_status', 'organization_config_id', 'mapping_file_id', 'ax_business_meaning']);

      // Get all keys and filter out hidden columns
      const allKeys = [
        ...new Set(validTableData.flatMap((item) => Object.keys(item)))
      ].filter(key => !columnsToHide.has(key));

      return (
        <div className={`w-full mt-3 ${allKeys.length > 7 ? "overflow-x-auto" : ""}`}>
          {tableName && (
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {tableName}
            </h4>
          )}
          <table className={`min-w-full bg-white border border-gray-200 rounded-lg text-xs ${allKeys.length > 7 ? "table-fixed" : ""}`}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap w-12">
                  <button
                    onClick={() =>
                      handleSelectAll(messageId, validTableData.length)
                    }
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                    title="Select all"
                  >
                    {Array.from({ length: validTableData.length }).every(
                      (_, i) => selectedRows[`${messageId}-${i}`]
                    ) ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                {allKeys.map((key) => {
                  const headerMappings = {
                    sap_object_name: "SAP Object Name",
                    sap_table_name: "SAP Table Name",
                    sap_column_name: "SAP Column Name",
                    sap_description: "SAP Description",
                    sap_mandatory: "SAP Mandatory",
                    ax_table_name: "AX Table Name",
                    ax_field_name: "AX Field Name",
                    overall_confidence: "Overall Confidence",
                    SAPTableName: "SAP Table Name",
                    SAPColumnName: "SAP Column Name",
                    AXTableName: "AX Table Name",
                    AXColumnName: "AX Column Name",
                    ObjectName: "Object Name",
                    Description: "Description",
                    Mandatory: "Mandatory",
                  };

                  let headerText;
                  if (headerMappings[key]) {
                    headerText = headerMappings[key];
                  } else if (key.includes('_')) {
                    // Handle snake_case: replace underscores with spaces and capitalize
                    headerText = key
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                  } else {
                    // Handle camelCase: add space before capitals
                    headerText = key.replaceAll(/([a-z])([A-Z])/g, "$1 $2");
                  }
                  return (
                    <th
                      key={key}
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap"
                    >
                      {headerText}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {validTableData
                .slice(0, visibleRows[messageId] ? validTableData.length : INITIAL_ROW_LIMIT)
                .map((item, rowIndex) => {
                const rowKey = `${messageId}-${rowIndex}`;
                const isSelected = selectedRows[rowKey];
                const uniqueKey = `${messageId}-row-${item.id || item.SAPTableName || item.sap_table_name || rowIndex}`;
                return (
                  <tr
                    key={uniqueKey}
                    className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                  >
                    <td className="px-3 py-2 text-xs border-b w-12">
                      <button
                        onClick={() => handleRowSelect(messageId, rowIndex)}
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    {allKeys.map((key) => {
                      const value = item[key] || "";
                      const displayValue =
                        typeof value === "object" && value !== null
                          ? JSON.stringify(value)
                          : String(value);
                      const keyLower = key.toLowerCase();
                      const isStatus = keyLower.includes("status");
                      const isMandatory = keyLower.includes("mandatory");
                      const isConfidence = keyLower.includes("confidence");
                      const isSpecialKey = keyLower.includes("column") ||
                        keyLower.includes("table") ||
                        keyLower.includes("name");

                      let cellContent;
                      if (isStatus) {
                        const badgeClass = getStatusBadgeClass(displayValue);
                        cellContent = (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}>
                            {displayValue}
                          </span>
                        );
                      } else if (isMandatory) {
                        const badgeClass = getMandatoryBadgeClass(displayValue);
                        cellContent = (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}>
                            {displayValue}
                          </span>
                        );
                      } else if (isConfidence) {
                        // Handle confidence as percentage with color coding
                        const confidenceValue = Number.parseFloat(displayValue);
                        if (Number.isFinite(confidenceValue)) {
                          const percentage = (confidenceValue * 100).toFixed(1);
                          const badgeClass = getConfidenceBadgeClass(confidenceValue);
                          cellContent = (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}>
                              {percentage}%
                            </span>
                          );
                        } else {
                          cellContent = <span>{displayValue}</span>;
                        }
                      }
                      else {
                        cellContent = (
                          <span className={isSpecialKey ? "font-medium" : ""}>
                            {displayValue}
                          </span>
                        );
                      }
                      return (
                        <td
                          key={key}
                          className={`px-2 py-2 text-xs text-gray-900 border-b whitespace-nowrap overflow-hidden text-ellipsis ${allKeys.length > 7 ? "min-w-32" : "max-w-xs"}`}
                          title={displayValue}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
      {validTableData.length > INITIAL_ROW_LIMIT && !visibleRows[messageId] && (
              <div className="mt-1 text-center">
                <button
                  onClick={() => handleLoadMore(messageId)}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                >
                  Show more... ({validTableData.length - INITIAL_ROW_LIMIT} more rows)
                </button>
              </div>
            )}
          </div>
        );
    } catch (error) {
      console.error("Error in renderTableFromJSON:", error);
      return (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            Error rendering table data: {error.message}
          </p>
        </div>
      );
    }
  };

  // ------------- Sending messages & handling responses -------------
  const processStringData = (dataString) => {
    try {
      const parsedData = JSON.parse(dataString);
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        return {
          aiResponseData: parsedData,
          aiResponseText: "Here are the results:",
          hasDownloadableData: true,
        };
      }
      if (parsedData && typeof parsedData === "object") {
        return {
          aiResponseData: parsedData,
          aiResponseText: parsedData.message || parsedData.detail || "Here are the results:",
          hasDownloadableData: false,
        };
      }
      return {
        aiResponseData: null,
        aiResponseText: dataString,
        hasDownloadableData: false,
      };
    } catch (parseError) {
      console.error("Error parsing JSON data:", parseError);
      return {
        aiResponseData: null,
        aiResponseText: dataString,
        hasDownloadableData: false,
      };
    }
  };

  const processObjectDataResponse = (dataObj) => {
    const isSingleMessage = dataObj.message &&
      typeof dataObj.message === "string" &&
      Object.keys(dataObj).length === 1;
    const isSingleDetail = dataObj.detail &&
      typeof dataObj.detail === "string" &&
      Object.keys(dataObj).length === 1;

    if (isSingleMessage) {
      return {
        aiResponseData: null,
        aiResponseText: dataObj.message,
        hasDownloadableData: false,
      };
    }
    if (isSingleDetail) {
      return {
        aiResponseData: null,
        aiResponseText: dataObj.detail,
        hasDownloadableData: false,
      };
    }
    return {
      aiResponseData: dataObj,
      aiResponseText: dataObj.message || dataObj.detail || "Here are the results:",
      hasDownloadableData: true,
    };
  };

  const processAPIResponse = (apiResult) => {
    if (typeof apiResult.data === "string") {
      return processStringData(apiResult.data);
    }
    if (Array.isArray(apiResult.data)) {
      return {
        aiResponseData: apiResult.data,
        aiResponseText: "Here are the results:",
        hasDownloadableData: true,
      };
    }
    if (apiResult.data && typeof apiResult.data === "object") {
      return processObjectDataResponse(apiResult.data);
    }
    return {
      aiResponseData: null,
      aiResponseText: apiResult.data ? String(apiResult.data) : "No results found.",
      hasDownloadableData: false,
    };
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsTyping(true);

    try {
      const apiResult = await callAgentAPI(userMessage.text);

      if (apiResult.success) {
        const { aiResponseData, aiResponseText, hasDownloadableData } = processAPIResponse(apiResult);
        const aiResponse = {
          id: Date.now() + 1,
          text: aiResponseText,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: hasDownloadableData,
          originalQuery: userMessage.text,
          data: aiResponseData,
        };
        setMessages((prev) => [...prev, aiResponse]);
      } else {
        const errorResponse = {
          id: Date.now() + 1,
          text: `${apiResult.error}`,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: false,
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorResponse = {
        id: Date.now() + 1,
        text: `I apologize, but I'm currently unable to process your request due to a technical issue. Please try again later.`,
        sender: "ai",
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleDownload = async (msg, format) => {
    if (!msg.originalQuery) return;

    const result = await downloadResponse(msg.originalQuery, format);
    if (!result.success) {
      console.log("API download failed, trying fallback method...");
      if (msg.data) {
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replaceAll(":", "-");
        const filename = `mapping_result_${timestamp}`;
        const fallbackResult = generateFileFromDisplayedData(
          msg.data,
          format,
          filename
        );
        if (!fallbackResult.success) {
          toast.error(
            `Download failed: ${result.error}. Fallback also failed: ${fallbackResult.error}`
          );
        }
      } else {
        toast.error(`Download failed: ${result.error}`);
      }
    }
  };
  const spinnerStyle = `
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = spinnerStyle;
    document.head.appendChild(style);

    return () => {
      style.remove();
    };
  }, []);

  // ------------- Approve Button Renderer (to reduce nesting) -------------
  function renderApproveButton({ msg, selectedRows, approvingMessageId, approvalInProgress, handleApproveSelected }) {
    let actualTableData = null;

    if (Array.isArray(msg.data)) {
      actualTableData = msg.data;
    } else if (msg.data && typeof msg.data === "object") {
      const arrayKeys = Object.keys(msg.data).filter(
        (key) => Array.isArray(msg.data[key]) && msg.data[key].length > 0
      );
      if (arrayKeys.length > 0) {
        actualTableData = msg.data[arrayKeys[0]];
      }
    }

    if (!actualTableData || !Array.isArray(actualTableData) || actualTableData.length === 0) {
      return null;
    }

    const selectedIndices = [];
    actualTableData.forEach((_, index) => {
      if (selectedRows[`${msg.id}-${index}`]) {
        selectedIndices.push(index);
      }
    });

    if (selectedIndices.length === 0) {
      return null;
    }

    const isApproving = approvingMessageId === msg.id && approvalInProgress;

    const handleClick = () => {
      const selectedRowsData = selectedIndices.map(i => actualTableData[i]);
      handleApproveSelected(msg.id, selectedRowsData);
    };

    return (
      <button
        onClick={handleClick}
        disabled={isApproving}
        className={`flex items-center gap-1 px-3 py-1 text-xs text-white rounded-md transition-all ${isApproving ? 'bg-green-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'}`}
        title={isApproving ? "Processing..." : `Approve ${selectedIndices.length} selected mappings`}
      >
        {isApproving ? (
          <span>Approving...</span>
        ) : (
          <>
            <Check className="w-3 h-3" />
            <span>Approve Selected ({selectedIndices.length})</span>
          </>
        )}
      </button>
    );
  }

  // ------------- UI (responsive fullscreen layout) -------------
  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Header */}
      {/* <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-white border">
              <img src={logo1} alt="AI Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-lg font-semibold text-gray-800 leading-8">Bluegecko AI</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <img src={logo} alt="Falcon AI Logo" className="w-8 h-8 rounded-full object-cover cursor-pointer" />
        </div>
      </div> */}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <div className="max-w-[1100px] mx-auto space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.sender === "ai" && (
                <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src={logo1}
                    alt="AI Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className={`${msg.sender === "user" ? "max-w-3xl" : "max-w-5xl"}`}>
                <div className={`px-4 py-2 rounded-lg ${msg.sender === "user" ? "bg-blue-500 text-white" : "bg-white border border-gray-200 text-gray-800"}`}>
                  <p className="whitespace-pre-wrap">{parseMarkdown(msg.text)}</p>

                  {msg.sender === "ai" &&
                    msg.data &&
                    renderTableFromJSON(msg.data, msg.id)}

                  <span className="text-xs opacity-70 mt-1 block">
                    {msg.timestamp.toLocaleTimeString()}
                  </span>
                </div>

                {msg.sender === "ai" && msg.hasDownload && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(msg, "csv")}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        CSV
                      </button>
                    </div>
                    {/* APPROVE BUTTON */}
                    {renderApproveButton({
                      msg,
                      selectedRows,
                      approvingMessageId,
                      approvalInProgress,
                      handleApproveSelected,
                    })}
                  </div>
                )}
              </div>

              {msg.sender === "user" && (
                <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src={logo1}
                  alt="AI Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area (sticky) */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <div className="relative flex items-center">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="1"
              style={{ minHeight: "48px", maxHeight: "120px" }}
            />
            <button
              onClick={sendMessage}
              disabled={!message.trim() || isTyping}
              className={`absolute right-3 p-2 rounded-md transition-colors ${message.trim() && !isTyping ? "text-blue-500 hover:text-blue-600" : "text-gray-400 cursor-not-allowed"}`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Press Enter to send, Shift + Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

FalconAIChatbot.propTypes = {
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRefreshTable: PropTypes.func,
};

export default FalconAIChatbot;
